"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * One page can render more than one value-moment opt-in card for the same
 * target (the kundali result shows a compact card under the chart and the full
 * card after the reading). Once the seeker opts in through either card, every
 * card for that target shows the confirmation instead of asking again.
 *
 * Without a provider each card keeps its own state, so single-card surfaces
 * (matching, muhurat) behave exactly as before.
 */
type OptInRegistry = {
  optedIn: ReadonlySet<string>;
  markOptedIn: (key: string) => void;
};

const ValueMomentOptInContext = createContext<OptInRegistry | null>(null);

export function valueMomentOptInKey(target: { type: string; id: string }): string {
  return `${target.type}:${target.id}`;
}

export function ValueMomentOptInProvider({ children }: { children: ReactNode }) {
  const [optedIn, setOptedIn] = useState<ReadonlySet<string>>(() => new Set());
  const markOptedIn = useCallback((key: string) => {
    setOptedIn((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);
  const value = useMemo(() => ({ optedIn, markOptedIn }), [optedIn, markOptedIn]);
  return (
    <ValueMomentOptInContext.Provider value={value}>{children}</ValueMomentOptInContext.Provider>
  );
}

/** Shared opt-in state for one target; `null` when no provider is mounted. */
export function useSharedValueMomentOptIn(key: string): {
  optedIn: boolean;
  markOptedIn: () => void;
} | null {
  const registry = useContext(ValueMomentOptInContext);
  const markOptedIn = registry?.markOptedIn;
  const mark = useCallback(() => markOptedIn?.(key), [markOptedIn, key]);
  if (!registry) return null;
  return { optedIn: registry.optedIn.has(key), markOptedIn: mark };
}
